import React, { PropsWithChildren } from 'react';
import { Route, Routes } from 'react-router-dom';
import { t } from '@lingui/macro';
import { RoutePaths } from 'types/enums';
import { useLocalePrefixes } from 'hooks/useLocalePrefixes/useLocalePrefixes';
import { useStaticSeo } from 'hooks/useStaticSeo';
import { Browse } from './components/Browse/Browse';
import { Recommendation } from './components/Recommendation/Recommendation';
import { PackDetails } from './components/PackDetails/PackDetails';
import { RePackDetails } from './components/RePackDetails/RePackDetails';
import { ArtistDetails } from './components/ArtistDetails/ArtistDetails';
import { LabelDetails } from './components/LabelDetails/LabelDetails';
import { BrandtypeDetails } from './components/BrandtypeDetails/BrandtypeDetails';
import { DynamicMetadata } from './components/DynamicMetadata/DynamicMetadata';

const MetadataRouter: React.FC<PropsWithChildren> = ({ children }) => {
    const staticSeo = useStaticSeo();
    const localePrefixes = useLocalePrefixes();

    return (
        <>
            <Routes>
                {localePrefixes.map((locale) => (
                    <React.Fragment key={locale}>
                        <Route
                            path={`${locale}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={staticSeo.forYou.title}
                                    descriptionFallback={
                                        staticSeo.forYou.description
                                    }
                                    imageUrl={staticSeo.forYou.imageUrl}
                                    withGoogleSearch
                                    withBrandTitlePrefix={true}
                                />
                            }
                        />
                        <Route
                            path={`${locale}${RoutePaths.CreatorBeatMaker}/:hash`}
                            element={
                                <DynamicMetadata
                                    titleFallback={
                                        staticSeo.creatorShared.title
                                    }
                                    descriptionFallback={
                                        staticSeo.creatorShared.description
                                    }
                                    imageUrl={staticSeo.creatorShared.imageUrl}
                                    withWildcard={true}
                                />
                            }
                        />
                        <Route
                            path={`${locale}${RoutePaths.CreatorBeatMaker}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={staticSeo.creator.title}
                                    descriptionFallback={
                                        staticSeo.creator.description
                                    }
                                    imageUrl={staticSeo.creator.imageUrl}
                                />
                            }
                        />
                        <Route
                            path={`${locale}${RoutePaths.Instruments}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={staticSeo.instruments.title}
                                    descriptionFallback={
                                        staticSeo.instruments.description
                                    }
                                    imageUrl={staticSeo.instruments.imageUrl}
                                />
                            }
                        />
                        <Route
                            path={`${locale}${RoutePaths.Genres}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={staticSeo.genres.title}
                                    descriptionFallback={
                                        staticSeo.genres.description
                                    }
                                    imageUrl={staticSeo.genres.imageUrl}
                                />
                            }
                        />
                        <Route
                            path={`${locale}${RoutePaths.Sfxs}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={staticSeo.sfxs.title}
                                    descriptionFallback={
                                        staticSeo.sfxs.description
                                    }
                                    imageUrl={staticSeo.sfxs.imageUrl}
                                />
                            }
                        />
                        <Route
                            path={`${locale}${RoutePaths.Browse}`}
                            element={<Browse />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Genres}/:genres`}
                            element={<Browse />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Descriptors}/:descriptors`}
                            element={<Browse />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Instruments}/:instruments`}
                            element={<Browse />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Sfxs}/:sfsx`}
                            element={<Browse />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Recommendations}/:id`}
                            element={<Recommendation />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Packs}/:slug`}
                            element={<PackDetails />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Collections}/:slug`}
                            element={<RePackDetails />}
                        />

                        <Route
                            path={`${locale}${RoutePaths.Charts}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={staticSeo.charts.title}
                                    descriptionFallback={
                                        staticSeo.charts.description
                                    }
                                    imageUrl={staticSeo.charts.imageUrl}
                                />
                            }
                        />
                        <Route
                            path={`${locale}${RoutePaths.Charts}/:slug`}
                            element={<RePackDetails />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.Exclusives}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={staticSeo.exclusives.title}
                                    descriptionFallback={
                                        staticSeo.exclusives.description
                                    }
                                    imageUrl={staticSeo.exclusives.imageUrl}
                                />
                            }
                        />

                        <Route
                            path={`${locale}${RoutePaths.Artists}/:slug`}
                            element={<ArtistDetails />}
                        />

                        <Route
                            path={`${locale}${RoutePaths.Labels}/:slug`}
                            element={<LabelDetails />}
                        />

                        <Route
                            path={`${locale}${RoutePaths.Category}/:slug`}
                            element={<BrandtypeDetails />}
                        />
                        <Route
                            path={`${locale}${RoutePaths.KeyBpmFinder}`}
                            element={
                                <DynamicMetadata
                                    titleFallback={t`Free Song Key & BPM Finder`}
                                    descriptionFallback={t`Upload your song to find its key and BPM. Our Key & BPM Finder tool will analyze your song and give an accurate estimate of its key and BPM. Try it for free!`}
                                />
                            }
                        />
                    </React.Fragment>
                ))}
                {/* Fallback to default Metadata */}
                <Route
                    path="*"
                    element={
                        <DynamicMetadata
                            titleFallback={staticSeo.default.title}
                            descriptionFallback={staticSeo.default.description}
                            imageUrl={staticSeo.default.imageUrl}
                        />
                    }
                />
            </Routes>
            {children}
        </>
    );
};

export default MetadataRouter;
